import { useCallback, useState } from 'react';
import { useIsMounted } from '@bymason/util-hooks';
import { Slide, SlideProps } from '../../mui/Slide';
import { Snackbar } from '../../mui/Snackbar';
import { ControllerAlert } from '../ControllerAlert';

export type ControllerToastSeverity = 'error' | 'info' | 'success' | 'warning';

export interface ControllerToastProps {
  message: React.ReactNode;
  onClose?: () => void;
  severity: ControllerToastSeverity;
  overrideHideTimeout?: null | number;
}

const SlideTransition = (props: Omit<SlideProps, 'direction'>) => <Slide {...props} direction='down' />;

export function ControllerToast({ message, onClose, severity, overrideHideTimeout }: ControllerToastProps) {
  const isMounted = useIsMounted();
  const [open, setOpen] = useState(!!message);

  const getHideDuration = useCallback(() => {
    if (overrideHideTimeout || overrideHideTimeout === null) {
      return overrideHideTimeout;
    }
    switch (severity) {
      case 'error':
      case 'warning':
        return null;
      case 'info':
      case 'success':
        return 6000;
    }
  }, [overrideHideTimeout, severity]);

  const handleClose = useCallback(
    (_, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }

      if (onClose) {
        onClose();
      }

      if (isMounted()) {
        setOpen(false);
      }
    },
    [isMounted, onClose]
  );

  return (
    <Snackbar
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      autoHideDuration={getHideDuration()}
      onClose={handleClose}
      open={open}
      TransitionComponent={SlideTransition}
      sx={{ maxWidth: 560 }}
    >
      <ControllerAlert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
        {message}
      </ControllerAlert>
    </Snackbar>
  );
}
